/**
 * Back-to-top control.
 *
 * The tricky part in this theme is *what* actually scrolls. Depending on the
 * template, the scroller may be the document or a nested overflow container, so
 * neither `window.scrollTo` nor a `scroll` listener on `window` is reliable on its
 * own. Instead we listen for `scroll` in the capture phase — `scroll` does not
 * bubble, so capture is the only way to hear about a nested scroller from the
 * document — and treat whatever fired the event as the scroller. That element is
 * then the one we scroll back, so the button works in both cases without having to
 * hard-code a selector that a future layout change would silently break.
 */

const SHOW_AFTER_PX = 600;

/** @type {Element | Document | null} */
let scroller = null;

/**
 * @param {Element | Document} target
 * @returns {number}
 */
function scrollTopOf(target) {
  if (target === document || target === document.documentElement || target === document.body) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  return /** @type {Element} */ (target).scrollTop;
}

function init() {
  const button = document.querySelector('[data-back-to-top]');
  if (!(button instanceof HTMLElement)) return;

  let ticking = false;

  /**
   * @param {Event} event
   */
  function onScroll(event) {
    const target = /** @type {Element | Document} */ (event.target);
    // Ignore scrolls from small nested widgets (carousels, drawers); only adopt a
    // scroller once it has actually travelled a meaningful distance vertically.
    const top = scrollTopOf(target);
    if (scroller !== target && top < SHOW_AFTER_PX) return;
    scroller = target;

    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      button.toggleAttribute('data-visible', scrollTopOf(scroller ?? document) > SHOW_AFTER_PX);
    });
  }

  document.addEventListener('scroll', onScroll, { passive: true, capture: true });

  button.addEventListener('click', () => {
    const target = scroller ?? document.scrollingElement ?? document.documentElement;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    if (target === document || target === document.scrollingElement || target === document.documentElement) {
      window.scrollTo({ top: 0, behavior });
    } else {
      /** @type {Element} */ (target).scrollTo({ top: 0, behavior });
    }

    // Move focus somewhere sensible rather than leaving it on a now-hidden button.
    const main = document.getElementById('MainContent');
    if (main) {
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
      main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
